import { INTAKE_STEPS, intakeVocabulary, isIntakeStep, isStepAnswered, MOTIVATION_STEP, ONBOARDING_CHOICE_VALUES, PAIN_AREAS_STEP, pathFor, } from "@repo/contract/training";
/**
 * The stepped intake's UI state (`D126`) — order, progress, Back, resume.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ── TWO HALVES, ONE RULE (plan D52) ─────────────────────────────────────────
 * Which answer writes what is `@repo/contract/training`'s `intake.ts`: the
 * path, what counts as answered, the reachable answers and the compile are
 * there, because the trainer's write tool must use the same mapping. This file
 * is the half only a screen needs — which question is on screen, how many are
 * left, where Back goes, which control draws a step, and where a half-finished
 * intake is kept. Nothing here decides a write.
 *
 * ── WHY THERE IS NO REACT IN THIS FILE ──────────────────────────────────────
 * `D126` moved Session 1 out of the chat because the chat could not answer
 * three questions: where am I, how much is left, and how do I go back. All
 * three are decided here, so all three can be walked forward and backward in
 * a test. The screen renders what this returns and decides nothing.
 *
 * ── THIS MODEL AND THE SERVER'S PROGRESS DISAGREE ON PURPOSE ────────────────
 * The server derives progress from STORED rows, and under `D126` nothing is
 * stored until the end — so for the whole intake `GET /me/onboarding` reports
 * `next: "date_of_birth"` or `"goal"` while the person is on step fourteen.
 * The server is the truth about what is stored; this model is the truth about
 * where the person is. They meet once, at the commit.
 *
 * ── PURE ───────────────────────────────────────────────────────────────────
 * No clock, no ids, no network. The store below is a port, injected.
 * ═══════════════════════════════════════════════════════════════════════════
 */
// The contract's half, re-exported so a screen imports the intake from one
// place. Nothing is restated.
export { BODY_FIGURE_VIEW_BOX, BODY_MAP_REGIONS, decodePainReport, encodePainReport, equipmentIdsOf, FIGURE_OUTLINES, fromCurrentRow, HOW_HEARD_STEP, INTAKE_STEPS, isCatalogueId, isComplete, isIntakeStep, MOTIVATION_STEP, PAIN_AREAS_STEP, painReportsOf, pathFor, reachableAnswers, toCurrentRow, } from "@repo/contract/training";
// ── Walking ─────────────────────────────────────────────────────────────────
/**
 * The first step on the path with no answer, or `null` when the intake is
 * finished. Forward navigation and resume are the same question.
 */
export function nextStep(answers) {
    return (pathFor(answers).find((step) => !isStepAnswered(answers, step)) ?? null);
}
/**
 * The step before `current`, or `null` at the first one. A `current` the
 * branches have taken OFF the path still gets an answer — the step before
 * where it would sit — because the person is looking at it and Back must work.
 */
export function previousStep(answers, current) {
    const path = pathFor(answers);
    const index = path.indexOf(current);
    if (index > 0) {
        return path[index - 1] ?? null;
    }
    if (index === 0) {
        return null;
    }
    const ordinal = INTAKE_STEPS.indexOf(current);
    if (ordinal < 0) {
        return null;
    }
    const before = path.filter((step) => INTAKE_STEPS.indexOf(step) < ordinal);
    return before.at(-1) ?? null;
}
/**
 * The step after `current` on the path, or `null` at the last one — the
 * review's forward rule (`D155`).
 *
 * On a re-entry every step arrives answered (`prefill.ts`), so
 * {@link nextStep} is `null` from the start and confirming question one would
 * land on the finish card. The review therefore advances by POSITION. A branch
 * the edit just opened is the next step on the path, so it is asked in its
 * place, unanswered.
 */
export function stepAfter(answers, current) {
    const path = pathFor(answers);
    const index = path.indexOf(current);
    if (index >= 0) {
        return path[index + 1] ?? null;
    }
    const ordinal = INTAKE_STEPS.indexOf(current);
    return path.find((step) => INTAKE_STEPS.indexOf(step) > ordinal) ?? null;
}
/**
 * Where the person is, and out of how many. `index` is 1-based and `total` is
 * the path's length — an undecided branch counted at its longest, so the total
 * only ever shrinks going forward and the fraction never moves backwards. A
 * `current` the branches excluded is counted in at its own position: the
 * person is on it.
 */
export function progressFor(answers, current) {
    const path = pathFor(answers);
    const walked = path.includes(current)
        ? path
        : INTAKE_STEPS.filter((step) => path.includes(step) || step === current);
    const index = walked.indexOf(current);
    return index < 0
        ? { index: 0, total: walked.length }
        : { index: index + 1, total: walked.length };
}
/** How many steps of the current path are answered. Answers stranded off the
 *  path do not count. */
export function answeredCount(answers) {
    return pathFor(answers).filter((step) => isStepAnswered(answers, step))
        .length;
}
/**
 * Answers a changed branch has made unreachable — KEPT, so switching back
 * restores them untouched, and never compiled: the contract's
 * `compileIntake` reads `reachableAnswers`, not the raw set. Deleting them
 * would lose work `D126` promises to keep; writing them would `PUT` a variable
 * week that names fixed days.
 */
export function strandedSteps(answers) {
    const path = new Set(pathFor(answers));
    return INTAKE_STEPS.filter((step) => !path.has(step) && isStepAnswered(answers, step));
}
// ── The control each step draws ─────────────────────────────────────────────
const NONE = [];
/**
 * The seven PAR-Q+ questions as label keys, in the questionnaire's order.
 * ONE screen, not seven steps: `parq` is one entry in `ONBOARDING_STEPS`,
 * `PUT /me/parq` takes a seven-tuple, and the instrument is a form read whole.
 */
export const PARQ_QUESTION_KEYS = Object.freeze((ONBOARDING_CHOICE_VALUES.parq ?? NONE).map((index) => `onboarding.parq.${index}`));
/** How many examples `D129` shows under the field. */
const MOTIVATION_EXAMPLES = 3;
/** Multi-select steps. `equipment` decides its own. */
const MULTIPLE_CHOICE_STEPS = ["fixed_weekdays"];
/**
 * A choice ask built on the device — the options' label keys are
 * `onboarding.<step>.<value>`, the same keys the chat's asks used. This was
 * `localChoiceAsk` in the chat module; it belongs to the intake now.
 */
function choiceAsk(step, values, multiple) {
    const options = values.map((value) => ({
        labelKey: `onboarding.${step}.${value}`,
        value,
    }));
    return multiple
        ? { kind: "choice", multiple: true, options, step }
        : { kind: "choice", options, step };
}
/**
 * The control for a step, given what has been answered before it.
 *
 * The two numeric ranges (height 100–250 cm by 1, weight 30–300 kg by 0.1)
 * are the server script's (`script.ts`), which this bundle cannot import;
 * `profileInput` bounds the height the same way.
 */
export function controlFor(step, answers) {
    switch (step) {
        case "date_of_birth":
            return { kind: "date", step };
        case "height":
            return {
                kind: "number",
                max: 250,
                min: 100,
                step,
                stepBy: 1,
                unit: "cm",
            };
        case "weight":
            return {
                kind: "number",
                max: 300,
                min: 30,
                step,
                stepBy: 0.1,
                unit: "kg",
            };
        case "parq":
            return { kind: "parq", questionKeys: PARQ_QUESTION_KEYS, step };
        case PAIN_AREAS_STEP:
            return { kind: "screen", screen: "body-map", step };
        case MOTIVATION_STEP:
            return {
                exampleKeys: Array.from({ length: MOTIVATION_EXAMPLES }, (_unused, index) => `onboarding.motivation.example_${index + 1}`),
                kind: "text",
                skippable: true,
                step,
            };
        default:
            return vocabularyControl(step, answers);
    }
}
/**
 * The choice steps, and `equipment`, from the contract's vocabulary — so the
 * options drawn and the options `isStepAnswered` accepts are one list. A gym
 * or a mix gets the catalogue checklist screen; home and outdoors the four
 * chips.
 */
function vocabularyControl(step, answers) {
    const vocabulary = intakeVocabulary(step, answers);
    if (vocabulary.kind === "catalogue_ids") {
        return { kind: "screen", screen: "equipment", step };
    }
    if (vocabulary.kind !== "values" || vocabulary.values.length === 0) {
        return null;
    }
    return choiceAsk(step, vocabulary.values, step === "equipment" || MULTIPLE_CHOICE_STEPS.includes(step));
}
// ── Resume, outside the process ─────────────────────────────────────────────
/**
 * The storage key. Kept byte for byte from the Expo build
 * (`mypocketgym.onboarding.intake`), so a future migration of an installed
 * device's value is a copy.
 */
export const INTAKE_STORAGE_KEY = "mypocketgym.onboarding.intake";
/**
 * Bumped when the stored shape changes. A set written by another version is
 * DROPPED, never migrated: under `D126` nothing is written to the server until
 * the intake completes, so misreading this is a profile, a config, a PAR-Q and
 * an injury list built from fields that meant something else, all at once.
 * Bump it for a change where an old value would DECODE as something else.
 */
export const INTAKE_VERSION = 1;
/**
 * Keep the answers and the step on screen, scoped to the account: signing
 * out and in as somebody else must never resume — and then commit — the first
 * person's half-finished intake.
 */
export async function rememberIntake(accountId, answers, step, store) {
    try {
        await store.setItem(INTAKE_STORAGE_KEY, JSON.stringify({ accountId, answers, step, v: INTAKE_VERSION }));
    }
    catch {
        // Storage refused. The answer is still on screen and the intake still
        // works; only resuming after leaving is lost, and throwing here would
        // take the answer with it.
    }
}
export async function forgetIntake(store) {
    try {
        await store.removeItem(INTAKE_STORAGE_KEY);
    }
    catch {
        // As above. A stale set is checked against the account and the version
        // before any of it is believed.
    }
}
/**
 * The intake to resume, or `null` — for another account, another version, or
 * anything that did not parse. The stored step is honoured while it is still
 * on the path; otherwise the answers still load and {@link nextStep} decides.
 */
export async function readIntake(accountId, store) {
    let raw = null;
    try {
        raw = await store.getItem(INTAKE_STORAGE_KEY);
    }
    catch {
        return null;
    }
    if (raw === null) {
        return null;
    }
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return null;
    }
    return readResumption(parsed, accountId);
}
function readResumption(value, accountId) {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return null;
    }
    const record = value;
    if (record.v !== INTAKE_VERSION || record.accountId !== accountId) {
        return null;
    }
    return resumptionOf(record);
}
/** The answers, and the stored step while it is still on their path. */
function resumptionOf(record) {
    const answers = readAnswers(record.answers);
    const stored = record.step;
    const path = pathFor(answers);
    const step = isIntakeStep(stored) && path.includes(stored) ? stored : nextStep(answers);
    return { answers, step };
}
/** Keys this build does not know are DROPPED rather than carried: a future
 *  build's step is a shape whose meaning this one cannot know. */
function readAnswers(value) {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return {};
    }
    const out = {};
    for (const [step, entry] of Object.entries(value)) {
        if (isIntakeStep(step) &&
            Array.isArray(entry) &&
            entry.every((item) => typeof item === "string")) {
            out[step] = entry;
        }
    }
    return out;
}
// ── Before an account exists (`D262`) ───────────────────────────────────────
/**
 * Where the intake is kept while the person has NO account yet (`D262`): the
 * same answers and step as {@link rememberIntake}, under a key of its own and
 * scoped to nothing, because there is no account to scope to.
 *
 * ── A KEY OF ITS OWN, NOT THE ACCOUNT KEY WITH A NULL ACCOUNT ─────────────
 * Two reasons. A sign-in by a DIFFERENT account than the one this device last
 * held runs the per-account cleanup (`data/provider.tsx`, T35), which removes
 * {@link INTAKE_STORAGE_KEY} — before the answers the person just gave could
 * reach the account they were signing in to. And an older bundle (an OTA
 * rollback) never reads this key, so it cannot take a guest's answers for an
 * account's: it shows its own sign-in, starts its own intake, and these
 * answers wait for a bundle that knows them.
 *
 * Written on every step, read when the intake opens without an account, and
 * settled once after sign-in: adopted into the account's own record, or
 * discarded when the account had already finished its intake
 * (`apps/mobile/src/features/onboarding/hand-off.ts`).
 */
export const GUEST_INTAKE_STORAGE_KEY = "mypocketgym.onboarding.guest";
/**
 * The guest record's shape version. {@link INTAKE_VERSION}'s rule: a record
 * from another version is DROPPED, never migrated, because nothing of it
 * reaches the server until the commit, and a misread field would be written
 * as somebody's profile.
 */
export const GUEST_INTAKE_VERSION = 1;
export async function rememberGuestIntake(answers, step, store) {
    try {
        await store.setItem(GUEST_INTAKE_STORAGE_KEY, JSON.stringify({ answers, step, v: GUEST_INTAKE_VERSION }));
    }
    catch {
        // As `rememberIntake`: the answer stays on screen; only the resume after
        // leaving the app is lost.
    }
}
/**
 * The guest's intake, or `null` — none, another version, or anything that did
 * not parse. A record that carries an `accountId` is not a guest's and is
 * refused: nothing here resumes an account's answers without its account.
 */
export async function readGuestIntake(store) {
    let raw = null;
    try {
        raw = await store.getItem(GUEST_INTAKE_STORAGE_KEY);
    }
    catch {
        return null;
    }
    if (raw === null) {
        return null;
    }
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return null;
    }
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        return null;
    }
    const record = parsed;
    if (record.v !== GUEST_INTAKE_VERSION || "accountId" in record) {
        return null;
    }
    return resumptionOf(record);
}
export async function forgetGuestIntake(store) {
    try {
        await store.removeItem(GUEST_INTAKE_STORAGE_KEY);
    }
    catch {
        // A record that outlives its sign-in is settled again the next time the
        // account's progress is known (`D262`).
    }
}
//# sourceMappingURL=intake.js.map